import React, { useState, useEffect } from 'react';
import { Save, Mail, MessageCircle, Clock, Zap, Sparkles, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import AutoRechargeCard from '../components/settings/AutoRechargeCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Schneidet das Versions-Suffix (`_v6`, `_v11`, `_v23`) am Ende von
 * Template-Namen ab — z. B. `password_reset_v7` → `password_reset`.
 * User-Wunsch: in der Zuordnungs-Übersicht soll die Versionsnummer
 * nicht ablenken, weil der Nutzer beim Auswählen ohnehin immer die
 * neueste Version bekommt.
 */
const stripTemplateVersion = (name) => {
  if (!name || typeof name !== 'string') return name || '';
  return name.replace(/_v\d+$/i, '');
};

// Mapping von Triggern zu empfohlenen WhatsApp-Template-Namen
const TRIGGER_TO_TEMPLATE_MAPPING = {
  'appointment_created': ['gebuchtertermin', 'terminbestätigung', 'termin_gebucht'],
  'appointment_upcoming': ['terminerinnerung', 'termin_reminder'],
  'appointment_changed': ['terminaenderung', 'termin_geaendert'],
  'appointment_cancelled': ['terminstornierung', 'termin_storniert'],
  'appointment_cancellation_confirmed': ['stornierung_best', 'stornierung_bestätigt', 'stornierung_confirmed'],
  'waitlist_joined': ['warteliste_eingetragen', 'warteliste_eintragung', 'waitlist_joined'],
  'waitlist_slot_available': ['warteliste_slot', 'warteliste_verfügbar'],
  'waitlist_auto_booked': ['warteliste_autogebucht', 'warteliste_gebucht'],
  'waitlist_consecutive_day': ['warteliste_folgetag'],
  'invoice_created': ['rechnung_neu', 'rechnung', 'invoice'],
  'invoice_paid': ['zahlungsbest', 'zahlungbest', 'zahlung_bestätigt', 'zahlungbestätigt', 'payment_confirmed'],
  'manual_invoice_reminder': ['zahlungerinnerung', 'rechnung_erinnerung'],
  'payment_failed': ['zahlungfehlgeschlagen', 'payment_failed'],
  'training_charge_failed': ['training_charge_failed', 'training_payment_failed'],
  'points_below_threshold': ['punktwarnung', 'punkte_niedrig'],
  'points_added': ['punkte_aufgeladen', 'points_added'],
  'subscription_credits_below_threshold': ['abowarnung', 'abo_credits', 'subscription_warning'],
  'subscription_renewed': ['aboerneuert', 'subscription_renewed'],
  'subscription_reload_failed': ['autoreload_fehlgeschlagen', 'reload_failed'],
  'first_appointment_completed': ['erstertermin', 'first_appointment'],
  'training_completed': ['feedback', 'training_feedback'],
  'customer_registered': ['willkommen', 'welcome'],
  'account_reactivation': ['reaktivierung', 'reactivation', 'willkommen_zurueck'],
  'series_created': ['serienbuchung', 'serie_gebucht'],
  'series_cancelled': ['serie_storniert', 'serie_cancelled'],
  'event_announcement': ['event', 'kampagne', 'promotion'],
  'campaign': ['kampagne', 'campaign', 'promotion'],
  'promotion': ['promotion', 'kampagne'],
  'payment_link_sent': ['payment_link', 'zahlungslink'],
  'rent_invoice_created': ['mietrechnung', 'rent_invoice'],
  'birthday': ['geburtstag', 'birthday'],
  // `passwort_reset_10` (Meta-Variante ohne `_v`) wird vom neuen
  // `extractVersion()`-Helfer als Version 10 erkannt — siehe oben.
  'password_reset_requested': ['passwort_reset', 'password_reset'],
  'reengagement_30d': ['reaktivierung_30d', 'reengagement_30d', 'reaktivierung_30'],
  'reengagement_60d': ['reaktivierung_60d', 'reengagement_60d', 'reaktivierung_60'],
  'holiday_reminder': ['holiday_reminder', 'feiertag_erinnerung'],
  'vacation_reminder': ['vacation_reminder', 'ferien_erinnerung'],
  'abandonment_recovery': ['abandonment_recovery', 'wiederaktivierung', 'neukunde_gutschein'],
  // Sharing-Flow (Trainer/Kunden teilen Termin-Slots)
  'sharing_invitation': ['sharing_invitation', 'teilen_einladung'],
  'sharing_invitation_sent': ['sharing_invitation_sent', 'teilen_gesendet'],
  'sharing_response_accepted': ['sharing_response_accepted', 'teilen_angenommen'],
  'sharing_response_rejected': ['sharing_response_rejected', 'teilen_abgelehnt'],
  // Win-Back-Funnel (3 Stufen) — die Trigger-Namen passen zu den Stages in
  // `backend/routes/winback_routes.py::_default_config()`.
  'winback_stage_1': ['winback_welcome', 'winback_stage_1', 'winback_1'],
  'winback_stage_2': ['winback_reminder', 'winback_stage_2', 'winback_2'],
  'winback_stage_3': ['winback_last_chance', 'winback_stage_3', 'winback_3'],
  // Passwordless Login (OTP via Email + WhatsApp).
  // `otp_login_code` ist ein Authentication-Template ohne Versionssuffix.
  'otp_login': ['otp_login_code', 'otp_login'],
  // Welle A — Punkte-Geschenk Notification an Empfänger
  'points_gift_received': ['punkte_geschenk_erhalten'],
  // Trial Follow-Up (Probetraining-Nachfass) — gefeuert vom täglichen
  // Cron `trial_followup_service.run_trial_followup_job`, wenn ein
  // Kunde nach einem Probetraining nicht erneut bucht.
  'trial_followup': ['trial_followup_wa', 'trial_followup']
};

const AutomationRulesPage = ({ embedded = false }) => {
  const [rules, setRules] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [dupEnabled, setDupEnabled] = useState(true);
  const [dupTemplateOk, setDupTemplateOk] = useState(false);
  const [dupSaving, setDupSaving] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchWhatsAppTemplates();
    fetchDupSettings();
  }, []);

  const fetchDupSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/customers/duplicates/settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setDupEnabled(d.enabled !== false);
        setDupTemplateOk(!!d.template_configured);
      }
    } catch (e) { /* noop */ }
  };

  const toggleDupDetection = async (checked) => {
    setDupSaving(true);
    setDupEnabled(checked);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/customers/duplicates/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: checked }),
      });
      if (!res.ok) throw new Error('Fehler');
      toast.success(checked ? 'Doppelte-Kunden-Erkennung aktiviert.' : 'Doppelte-Kunden-Erkennung deaktiviert.');
    } catch (e) {
      setDupEnabled(!checked);
      toast.error('Konnte Einstellung nicht speichern.');
    } finally {
      setDupSaving(false);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/automation/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchWhatsAppTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates?limit=500`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Include APPROVED and PENDING templates (PENDING can be pre-assigned)
        const usable = (data.data || []).filter(t => 
          t.status === 'APPROVED' || t.status === 'PENDING'
        );
        setWhatsappTemplates(usable);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
    }
  };

  const updateRule = (ruleId, updates) => {
    setRules(rules.map(rule => 
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  };

  // Helper to find the best matching template (preferring newest versions)
  /**
   * Findet das beste WhatsApp-Template für ein Set von Such-Patterns.
   *
   * User-Wunsch 05/2026: „Beim Klick auf 'Zuordnung' soll wirklich alles
   * zugeordnet werden." Vorher: `_v(\d+)$`-Regex erkannte nur Suffixe wie
   * `_v8`, nicht aber `_10` (Meta nimmt manchmal Unterstrich-Versionen,
   * wenn z. B. ein Marketing- in ein Utility-Template überführt wurde).
   * Resultat: `passwort_reset_10` galt als Version 0 und verlor gegen
   * jede `_v1`-Variante.
   *
   * Neuer Algorithmus:
   *   1. Bevorzugt APPROVED-Templates vor PENDING.
   *   2. Versions-Regex akzeptiert `_v10`, `_10`, `-10`, `_v10b`.
   *   3. Bei mehreren gleichen Versionsnummern gewinnt das Template mit
   *      dem grösseren `match.length / pattern.length`-Verhältnis (= das
   *      genauere Pattern). So gewinnt `passwort_reset` (vollständig)
   *      gegen ein hypothetisches `password_reset_test_v1`.
   */
  const extractVersion = (name) => {
    if (!name) return 0;
    const m = name.match(/(?:_v|_|-)(\d+)[a-z]?$/i);
    return m ? parseInt(m[1], 10) : 0;
  };

  const findBestTemplate = (patterns) => {
    if (!patterns || patterns.length === 0) return null;
    const scored = [];

    for (const pattern of patterns) {
      const pLower = pattern.toLowerCase();
      const pLen = pLower.length;
      whatsappTemplates.forEach((t) => {
        const nameLower = (t.name || '').toLowerCase();
        if (!nameLower.includes(pLower)) return;
        scored.push({
          tpl: t,
          // Higher = better match.
          score: {
            approved: t.status === 'APPROVED' ? 1 : 0,
            version: extractVersion(t.name),
            // Patterns-Längen-Verhältnis: bevorzugt spezifischere Matches.
            specificity: pLen / Math.max(nameLower.length, 1),
            pattern_rank: patterns.indexOf(pattern), // niedriger = wichtiger
          },
        });
      });
    }

    if (scored.length === 0) return null;

    scored.sort((a, b) => {
      const sa = a.score, sb = b.score;
      if (sa.approved !== sb.approved) return sb.approved - sa.approved;
      if (sa.version !== sb.version) return sb.version - sa.version;
      if (sa.pattern_rank !== sb.pattern_rank) return sa.pattern_rank - sb.pattern_rank;
      return sb.specificity - sa.specificity;
    });
    return scored[0].tpl;
  };

  // Automatische Zuordnung der WhatsApp-Templates basierend auf Trigger
  const autoAssignTemplates = (forceOverwrite = false) => {
    if (whatsappTemplates.length === 0) {
      toast.error('Keine genehmigten WhatsApp-Templates verfügbar');
      return;
    }

    setAutoAssigning(true);
    let assignedCount = 0;
    let skippedCount = 0;
    let updatedCount = 0;
    
    const updatedRules = rules.map(rule => {
      // Get possible template patterns for this trigger
      const patterns = TRIGGER_TO_TEMPLATE_MAPPING[rule.trigger] || [];
      
      // Find the best matching template (newest version)
      const matchedTemplate = findBestTemplate(patterns);
      
      if (!matchedTemplate) {
        return rule;
      }
      
      // Check if already has same template
      if (rule.whatsapp_template_name === matchedTemplate.name) {
        skippedCount++;
        return rule;
      }
      
      // Check if already assigned and not forcing overwrite
      if (rule.whatsapp_template_name && !forceOverwrite) {
        // Akzeptiert sowohl `_v10` als auch `_10` als Versionsangabe.
        const currentVersion = extractVersion(rule.whatsapp_template_name);
        const newVersion = extractVersion(matchedTemplate.name);

        if (newVersion > currentVersion) {
          // Newer version available, update it
          updatedCount++;
          return {
            ...rule,
            whatsapp_enabled: true,
            whatsapp_template_name: matchedTemplate.name,
            whatsapp_language: matchedTemplate.language || 'de'
          };
        }
        
        skippedCount++;
        return rule;
      }
      
      // Assign new or overwrite
      if (rule.whatsapp_template_name) {
        updatedCount++;
      } else {
        assignedCount++;
      }
      
      return {
        ...rule,
        whatsapp_enabled: true,
        whatsapp_template_name: matchedTemplate.name,
        whatsapp_language: matchedTemplate.language || 'de'
      };
    });

    setRules(updatedRules);
    setAutoAssigning(false);

    const total = assignedCount + updatedCount;
    if (total > 0) {
      let message = '';
      if (assignedCount > 0) message += `${assignedCount} neu zugeordnet`;
      if (updatedCount > 0) message += `${message ? ', ' : ''}${updatedCount} auf neuere Version aktualisiert`;
      toast.success(`✅ ${message}! Bitte speichern Sie die Änderungen.`);
    } else {
      toast.info('Keine neuen Zuordnungen möglich. Alle Regeln haben bereits die neueste Template-Version oder es wurden keine passenden gefunden.');
    }
  };

  const saveAllRules = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Save each rule and check responses
      const results = await Promise.all(
        rules.map(async (rule) => {
          const response = await fetch(`${BACKEND_URL}/api/automation/rules/${rule.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(rule)
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = `Fehler bei ${rule.id}`;
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.detail || errorMessage;
            } catch (e) {
              errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
          }
          return { success: true, ruleId: rule.id };
        })
      );

      toast.success('✅ Automatisierungs-Regeln gespeichert!');
    } catch (error) {
      console.error('Error saving rules:', error);
      toast.error(`Fehler beim Speichern: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setSaving(false);
    }
  };

  const getRuleTriggerText = (trigger) => {
    const triggers = {
      'appointment_created': 'Sofort nach Termin-Buchung',
      'appointment_upcoming': 'X Stunden vor Termin',
      'appointment_changed': 'Bei Terminänderung',
      'appointment_cancelled': 'Bei Terminstornierung',
      'waitlist_slot_available': 'Bei Wartelisten-Termin (Stornierung)',
      'waitlist_auto_booked': 'Bei automatischer Buchung von Warteliste',
      'waitlist_consecutive_day': 'Bei Folgetag-Reservierung (Bestätigung nötig)',
      'invoice_created': 'Bei Rechnungserstellung',
      'invoice_paid': 'Bei Zahlungseingang',
      'manual_invoice_reminder': 'Manuell auslösbar',
      'points_below_threshold': 'Wenn Punkte unter Schwellwert',
      'subscription_credits_below_threshold': 'Wenn Abo-Credits unter Schwellwert',
      'subscription_reload_failed': 'Bei fehlgeschlagenem Auto-Reload',
      'first_appointment_completed': '24h nach erstem Termin',
      'training_completed': 'Nach Training/Check-in',
      'customer_registered': 'Bei Kunden-Registrierung',
      'account_reactivation': 'Bei Konto-Reaktivierung',
      'series_created': 'Bei Serienbuchung',
      'series_cancelled': 'Bei Serie-Stornierung',
      'event_announcement': 'Bei Events/Workshops',
      'payment_link_sent': 'Beim Versenden eines Payment-Links',
      'holiday_reminder': 'X Tage vor Feiertag (Standard: 7 Tage)',
      'vacation_reminder': 'X Tage vor Schulferien (Standard: 21 Tage)',
      'abandonment_recovery': '4 Wochen nach Registrierung ohne Kauf (Neukunde-Wiederaktivierung)',
      'otp_login': 'Sofort bei passwortlosem Login (Email + WhatsApp Code)',
      'trial_followup': 'X Tage nach Probetraining ohne Folgebuchung (Standard: 14 Tage)',
      'app_install_followup_day2': 'X Tage nach Profil-Abschluss ohne App-Buchung (Standard: 2 Tage)'
    };
    return triggers[trigger] || trigger;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl">
      {/* Zurück Button */}
      {!embedded && (
      <div className="mb-4 flex items-center justify-between gap-4">
        <Button
          variant="outline"
          onClick={() => window.location.href = '/communication/settings'}
        >
          ← Zurück zur Kommunikation
        </Button>
        {/* Auto-Sync läuft seit 05/2026 vollautomatisch über
            `services/notification_seeds.py`: DB → JSON-Files alle 30 s,
            Files → DB bei jedem Pod-Start. Kein manueller Button mehr nötig. */}
      </div>
      )}
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Zap className="h-6 w-6" />
          Automatische Benachrichtigungen
        </h1>
        <p className="text-gray-600 mt-1">
          Legen Sie fest, wann welche Email- und WhatsApp-Nachrichten automatisch versendet werden
        </p>
      </div>

      {/* Quick Actions */}
      <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-green-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Schnelle Zuordnung
            </h3>
            <p className="text-sm text-green-700 mt-1">
              Ordnet automatisch genehmigte WhatsApp-Templates den passenden Regeln zu (bevorzugt neueste Version)
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => autoAssignTemplates(false)}
              disabled={autoAssigning || whatsappTemplates.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {autoAssigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Zuordnen...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Auto-Zuordnung
                </>
              )}
            </Button>
            <Button
              onClick={() => {
                if (window.confirm('⚠️ ALLE Zuordnungen überschreiben?\n\nDies ersetzt alle bestehenden WhatsApp-Template-Zuordnungen mit der besten passenden Vorlage.')) {
                  autoAssignTemplates(true);
                }
              }}
              disabled={autoAssigning || whatsappTemplates.length === 0}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
            >
              🔄 Alle neu zuordnen
            </Button>
          </div>
        </div>
        <div className="mt-2 text-xs text-green-600 flex items-center gap-4">
          <span>{whatsappTemplates.filter(t => t.status === 'APPROVED').length} genehmigte Templates</span>
          {whatsappTemplates.filter(t => t.status === 'PENDING').length > 0 && (
            <span className="text-amber-600">
              ⏳ {whatsappTemplates.filter(t => t.status === 'PENDING').length} warten auf Meta-Genehmigung
            </span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Auto-Aufladung bei leeren Punkten/Abo */}
        <AutoRechargeCard />

        {/* Doppelte-Kunden-Erkennung (eigene ein-/ausschaltbare Automatik-Regel) */}
        <Card className="border-purple-200" data-testid="dup-detection-rule">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Doppelte Kunden erkennen & vorschlagen
              </span>
              <div className="flex items-center gap-2 text-sm font-normal text-gray-600">
                <Clock className="h-4 w-4" /> Täglich 09:30
              </div>
            </CardTitle>
            <CardDescription>
              Findet wahrscheinliche Doppel-Logins (gleiche Telefonnummer oder gleicher Name) und
              schlägt sie dir per WhatsApp mit Ja/Nein vor. Der Account mit E-Mail bleibt; alle
              Daten werden übertragen (nichts wird gelöscht).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-600" />
                <Label className="font-medium">Automatik aktiv</Label>
              </div>
              <div className="flex items-center gap-2">
                {dupSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                <Switch
                  checked={dupEnabled}
                  onCheckedChange={toggleDupDetection}
                  disabled={dupSaving}
                  data-testid="dup-detection-toggle"
                />
              </div>
            </div>
            <p className="text-xs mt-2 flex items-center gap-2">
              {dupTemplateOk
                ? <span className="text-green-600">✓ WhatsApp-Vorlage „duplikat_alarm" hinterlegt</span>
                : <span className="text-amber-600">⏳ WhatsApp-Vorlage „duplikat_alarm" bei Meta einreichen (WhatsApp → Templates)</span>}
              <span className="text-gray-400">· Empfänger: Admin-Nummern (KI &amp; API → WhatsApp Bot)</span>
            </p>
          </CardContent>
        </Card>

        {rules.map(rule => (
          <Card key={rule.id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{rule.name}</span>
                <div className="flex items-center gap-2 text-sm font-normal text-gray-600">
                  <Clock className="h-4 w-4" />
                  {getRuleTriggerText(rule.trigger)}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <Label className="font-medium">Email senden</Label>
                    </div>
                    <Switch
                      checked={rule.email_enabled}
                      onCheckedChange={(checked) => updateRule(rule.id, { email_enabled: checked })}
                    />
                  </div>
                  {rule.email_enabled && (
                    <div className="text-xs text-gray-600 mt-2 bg-blue-50 p-2 rounded">
                      Template: {stripTemplateVersion(rule.email_template_id)}
                    </div>
                  )}
                </div>

                {/* WhatsApp */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-green-600" />
                      <Label className="font-medium">WhatsApp senden</Label>
                    </div>
                    <Switch
                      checked={rule.whatsapp_enabled}
                      onCheckedChange={(checked) => updateRule(rule.id, { whatsapp_enabled: checked })}
                    />
                  </div>
                  {rule.whatsapp_enabled && (
                    <div className="mt-2 space-y-2">
                      <Label className="text-xs">WhatsApp Template auswählen</Label>
                      <select
                        value={rule.whatsapp_template_name || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const selected = whatsappTemplates.find(t => t.name === value);
                          updateRule(rule.id, {
                            whatsapp_template_name: value,
                            whatsapp_language: selected?.language || 'de',
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">-- Bitte wählen --</option>
                        {whatsappTemplates.map(template => (
                          <option key={template.id} value={template.name}>
                            {template.status === 'PENDING' ? '⏳ ' : '✅ '}{template.name} ({template.language})
                          </option>
                        ))}
                      </select>
                      {whatsappTemplates.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ Keine WhatsApp Templates verfügbar. Bitte zuerst Templates hochladen.
                        </p>
                      )}
                      {rule.whatsapp_template_name && (
                        <div className={`text-xs mt-2 p-2 rounded ${
                          whatsappTemplates.find(t => t.name === rule.whatsapp_template_name)?.status === 'APPROVED'
                            ? 'text-green-700 bg-green-50'
                            : 'text-amber-700 bg-amber-50'
                        }`}>
                          {whatsappTemplates.find(t => t.name === rule.whatsapp_template_name)?.status === 'APPROVED' 
                            ? '✅' 
                            : '⏳'} Template: {stripTemplateVersion(rule.whatsapp_template_name)}
                          {whatsappTemplates.find(t => t.name === rule.whatsapp_template_name)?.status === 'PENDING' && (
                            <span className="ml-2 text-amber-600">(wartet auf Genehmigung)</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Hinweis: Schwellenwert für Punkte-Warnung wird in Einstellungen > Kommunikation > Erinnerungen konfiguriert */}

              {/* Admin Email (nur für Auto-Reload) */}
              {rule.trigger === 'subscription_reload_failed' && (
                <div className="mt-4 pt-4 border-t">
                  <div className="p-4 border rounded-lg bg-orange-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <Label className="font-medium">👨‍💼 Admin Email senden</Label>
                      </div>
                      <Switch
                        checked={rule.admin_email_enabled || false}
                        onCheckedChange={(checked) => updateRule(rule.id, { admin_email_enabled: checked })}
                      />
                    </div>
                    {rule.admin_email_enabled && (
                      <div className="text-xs text-gray-600 mt-2 bg-white p-2 rounded">
                        Template: {stripTemplateVersion(rule.admin_email_template_id || 'failed_reload_admin_email')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Kunden-Präferenzen */}
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={rule.respect_customer_preferences}
                  onChange={(e) => updateRule(rule.id, { respect_customer_preferences: e.target.checked })}
                  className="rounded"
                />
                <span>Kunden-Präferenzen respektieren (nur senden wenn Kunde Email/WhatsApp aktiviert hat)</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={fetchRules}>
          Zurücksetzen
        </Button>
        <Button onClick={saveAllRules} disabled={saving} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern...' : 'Alle Regeln speichern'}
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">ℹ️ Wie funktioniert das?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600 space-y-2">
          <p><strong>Beispiel:</strong> Bei "Terminbestätigung":</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Wenn Email UND WhatsApp aktiviert sind → Kunde erhält BEIDE Nachrichten</li>
            <li>Wenn nur Email aktiviert → Kunde erhält nur Email</li>
            <li>Mit "Kunden-Präferenzen": Nur wenn Kunde Email/WhatsApp in seinem Profil aktiviert hat</li>
          </ul>
          <p className="mt-3 text-xs text-amber-600">
            <strong>⚠️ Wichtig:</strong> WhatsApp-Templates müssen zuerst bei Meta erstellt und genehmigt werden.
            Siehe: Einstellungen → Kommunikation → WA Templates
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationRulesPage;
